import { CHART_ACCESS_DENIAL_REASONS, type ChartAccessDenialReason } from '@emr/shared';

import { isMemberOf } from '../../lib/isMemberOf.ts';

/**
 * A sentence for each reason `evaluateChartAccess` (`server/src/clinical/access.ts`) can refuse a
 * specific patient's chart — the `chart_access_denied` half of `api/client.ts`'s `ApiError`, which
 * carries the raw reason string but no wording of its own.
 *
 * `Record<ChartAccessDenialReason, string>` is the forcing function, matching `routes/AccessPage.tsx`'s
 * `CAPABILITY_COPY`: a reason added to `@emr/shared`'s closed union without a sentence here fails the
 * build rather than rendering a blank explanation.
 */
const CHART_ACCESS_DENIAL_COPY: Record<ChartAccessDenialReason, string> = {
  unrecognisedRole:
    'Your account has no role this build recognises, so no chart access could be evaluated.',
  roleCannotReadPatients: 'Your role does not include reading clinical charts.',
  noTreatmentRelationship: 'You have no recorded treatment relationship with this patient.',
  confidentialityLabel: 'This record carries a confidentiality label your access does not cover.',
  breakGlassMissingJustification:
    'Emergency access needs a written justification, which was not provided.',
  roleCannotBreakGlass:
    'Your role does not permit emergency access to a chart outside your care team.',
};

/** A sentence for `reason`, or a safe fallback for a value this build does not (yet) recognise —
 * the server and the client deploy independently, so an unrecognised reason is a real possibility,
 * not a defect to assume can never happen. */
export function chartAccessDenialMessage(reason: string | undefined): string {
  if (reason === undefined) return 'Access to this chart was refused.';
  if (isMemberOf(CHART_ACCESS_DENIAL_REASONS, reason)) return CHART_ACCESS_DENIAL_COPY[reason];
  return 'Access to this chart was refused, for a reason this build does not recognise.';
}
